import { RefObject, useCallback, useEffect, useRef, useState } from "react";

import { useTheme } from "@mui/material/styles";
import { isEventDiscoverabilityEligible } from "@simplyk/common";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useEventFeatureBannersVariant } from "@/features/Campaigns/tracking/useEventFeatureBannersVariant";
import {
  CHECK_IN_ACCORDION_EXPAND_ACTIONS,
  CheckInAccordionKey,
} from "@/features/CheckInToolsDrawer/helpers/useCheckInToolsDrawer";
import { InfoboxPointerPosition } from "@/dashboard-design-system/Infobox";
import { pointerWidth } from "@/dashboard-design-system/Infobox/constants";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";

export const CHECK_IN_TOOLS_DRAWER_ENTRY_POINT = "e_ticket_step";
export const CHECK_IN_TOOLTIP_MAX_WIDTH = 444;
export const QR_CARD_SELECTOR = ".ticket-qr-card";
export const CHECK_IN_TOOLTIP_DELAY_MS = 1000;

export interface QrCardAnchor {
  centerX: number;
  bottom: number;
  containerWidth: number;
}

export interface CheckInTooltipPlacement {
  top: number;
  left: number;
  width: number;
  pointer: InfoboxPointerPosition;
}

export const useCheckInTooltip = () => {
  const { liveFormData, apiFormObject, isMobilePreviewOpen } = useLiveFormEditorContext<"ticketing">();
  const { isSmallScreen, value: prefersReducedMotion } = useMediaQuery("(prefers-reduced-motion: reduce)");
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();
  const { variant: eventFeatureBannersVariant } = useEventFeatureBannersVariant();
  const [isTooltipDismissed, setIsTooltipDismissed] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [hasTooltipDelayElapsed, setHasTooltipDelayElapsed] = useState(false);
  const hasLoggedTooltipDisplayed = useRef(false);

  const eTicketPreviewInFocus = liveFormData?.eTicketPreviewInFocus ?? "e-ticket";
  const isEventForm = isEventDiscoverabilityEligible(apiFormObject.formCategory);
  const isETicketShown = eTicketPreviewInFocus === "e-ticket" && liveFormData?.ticketing.generateQrCode === true;
  const isPreviewVisible = !isSmallScreen || isMobilePreviewOpen;
  const canShowTooltip = isEventForm && isETicketShown && isPreviewVisible && !isTooltipDismissed && !isDrawerOpen;
  const isTooltipVisible = canShowTooltip && hasTooltipDelayElapsed;

  useEffect(() => {
    if (!canShowTooltip || hasTooltipDelayElapsed) {
      return;
    }
    const timeout = setTimeout(() => setHasTooltipDelayElapsed(true), CHECK_IN_TOOLTIP_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [canShowTooltip, hasTooltipDelayElapsed]);

  useEffect(() => {
    if (isTooltipVisible && !hasLoggedTooltipDisplayed.current) {
      hasLoggedTooltipDisplayed.current = true;
      logUserActionEvent(AmplitudeEvents.FormEticketCheckInTooltipDisplayed);
    }
  }, [isTooltipVisible, logUserActionEvent]);

  const dismissTooltip = () => {
    setIsTooltipDismissed(true);
    logUserActionEvent(AmplitudeEvents.FormEticketCheckInTooltipDismissed);
  };

  const openDrawerFromTooltip = () => {
    logUserActionEvent(AmplitudeEvents.FormEticketCheckInTooltipClicked);
    logUserActionEvent(AmplitudeEvents.CheckInToolsDrawerOpened, {
      entry_point: CHECK_IN_TOOLS_DRAWER_ENTRY_POINT,
      eventFeatureBannersVariant,
    });
    setIsTooltipDismissed(true);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
  };

  const logAccordionExpanded = (accordionKey: CheckInAccordionKey) => {
    logUserActionEvent(AmplitudeEvents.CheckInToolsDrawerActionClicked, {
      action: CHECK_IN_ACCORDION_EXPAND_ACTIONS[accordionKey],
      entry_point: CHECK_IN_TOOLS_DRAWER_ENTRY_POINT,
      eventFeatureBannersVariant,
    });
  };

  return {
    isEventForm,
    eTicketPreviewInFocus,
    isTooltipVisible,
    isDrawerOpen,
    isAnimationFrozen: isDrawerOpen || prefersReducedMotion,
    dismissTooltip,
    openDrawerFromTooltip,
    closeDrawer,
    logAccordionExpanded,
  };
};

interface UseCheckInTooltipPlacementParams {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  containerRef: RefObject<HTMLDivElement | null>;
  isEnabled: boolean;
}

export const useCheckInTooltipPlacement = ({
  iframeRef,
  containerRef,
  isEnabled,
}: UseCheckInTooltipPlacementParams) => {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<QrCardAnchor | null>(null);

  const measureAnchor = useCallback(() => {
    const iframe = iframeRef.current;
    const container = containerRef.current;
    const qrCard = iframe?.contentDocument?.querySelector(QR_CARD_SELECTOR);
    if (!iframe || !container || !qrCard) {
      setAnchor(null);
      return;
    }
    const iframeRect = iframe.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const qrCardRect = qrCard.getBoundingClientRect();
    setAnchor({
      centerX: iframeRect.left - containerRect.left + qrCardRect.left + qrCardRect.width / 2,
      bottom: iframeRect.top - containerRect.top + qrCardRect.bottom,
      containerWidth: containerRect.width,
    });
  }, [iframeRef, containerRef]);

  useEffect(() => {
    const container = containerRef.current;
    if (!isEnabled || !container) {
      return;
    }
    let animationFrame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(measureAnchor);
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(animationFrame);
    };
  }, [containerRef, isEnabled, measureAnchor]);

  const placement =
    isEnabled && anchor
      ? getCheckInTooltipPlacement({
          anchor,
          gutter: parseFloat(theme.spacing(2)),
          offsetFromAnchor: parseFloat(theme.spacing(1)),
          sidePointerCenterInset: parseFloat(theme.spacing(5)) + pointerWidth / 2,
        })
      : null;

  return { placement, measureAnchor };
};

interface GetCheckInTooltipPlacementParams {
  anchor: QrCardAnchor;
  gutter: number;
  offsetFromAnchor: number;
  sidePointerCenterInset: number;
}

export const getCheckInTooltipPlacement = ({
  anchor,
  gutter,
  offsetFromAnchor,
  sidePointerCenterInset,
}: GetCheckInTooltipPlacementParams): CheckInTooltipPlacement => {
  const width = Math.max(0, Math.min(CHECK_IN_TOOLTIP_MAX_WIDTH, anchor.containerWidth - gutter * 2));
  const top = anchor.bottom + offsetFromAnchor;
  const centeredLeft = anchor.centerX - width / 2;
  const maxLeft = anchor.containerWidth - gutter - width;

  if (centeredLeft >= gutter && centeredLeft <= maxLeft) {
    return { top, left: centeredLeft, width, pointer: "top" };
  }

  if (centeredLeft > maxLeft) {
    const left = Math.max(gutter, anchor.centerX + sidePointerCenterInset - width);
    return { top, left, width, pointer: "top-right" };
  }

  const left = Math.min(maxLeft, anchor.centerX - sidePointerCenterInset);
  return { top, left, width, pointer: "top-left" };
};
